import React, { useState, useEffect } from 'react';
import { Button, DateRangePicker, DateRangePickerProps, Popover, SpaceBetween, StatusIndicator, Select, Spinner, Input } from '@amzn/awsui-components-react/polaris';
import dayjs from 'dayjs';
import { getBrokerHealthState } from '../../api/api';
import CopyToClipboardButton from './copyToClipboardButton';

interface HealthStateIndicatorProps {
  healthState: string;
  requiredActions?: string;
  recommendedActions?: string;
  lastUpdatedTime?: string;
}

export const HealthStateIndicator: React.FC<HealthStateIndicatorProps> = ({ 
  healthState, 
  requiredActions, 
  recommendedActions,
  lastUpdatedTime
}) => {
  const normalizedHealthState = healthState?.toLowerCase();
  const isValidState = normalizedHealthState === 'red' || normalizedHealthState === 'yellow' || normalizedHealthState === 'green';
  const isUnknown = healthState === 'Unknown';
  
  if (isUnknown) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: '8px' }}>Unknown</span>
        <Popover 
          content={
            <div style={{ padding: '4px', maxWidth: '350px', color: '#000000' }}>
              {recommendedActions && (
                <div>
                  <strong>More Details:</strong>
                  <div style={{ marginTop: '4px' }}>
                    {recommendedActions}
                  </div>
                </div>
              )}
            </div>
          } 
          triggerType="text-inline"
        >
          <StatusIndicator type="error" />
        </Popover>
      </div>
    );
  }
  
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ marginRight: '8px' }}>
          {healthState && (
            <div style={{ display: 'flex', alignItems: 'center', color: '#000000' }}>
              {isValidState ? (
                <div 
                  style={{
                    width: '25px',
                    height: '25px',
                    borderRadius: '50%',
                    backgroundColor: getColorForState(healthState),
                    marginRight: '5px'
                  }}
                />
              ) : null}
              <span>{healthState || 'Unknown'}</span>
              {lastUpdatedTime && (
                <span style={{ 
                  marginLeft: '10px', 
                  fontSize: '12px', 
                  color: '#687078',
                  fontStyle: 'italic'
                }}>
                  Last updated: {lastUpdatedTime}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {!isUnknown && <div style={{ marginTop: '8px' }}>
        {requiredActions && (
          <div style={{ 
            padding: '0',
            fontSize: '14px',
            color: '#000000',
            maxWidth: '500px'
          }}>
            <div>
              <strong>Required Customer Actions:</strong>
            </div>
            {requiredActions.split(', ').map((action, index) => (
              <div key={index} style={{ marginLeft: '10px', display: 'flex' }}>
                <span style={{ marginRight: '5px' }}>.</span>
                <span>{action}</span>
              </div>
            ))}
          </div>
        )}
        {recommendedActions && (
          <div style={{ 
            padding: '0',
            fontSize: '14px',
            color: '#000000',
            maxWidth: '500px'
          }}>
            <div>
              <strong>Recommended Customer Actions:</strong>
            </div>
            {recommendedActions.split(', ').map((action, index) => (
              <div key={index} style={{ marginLeft: '10px', display: 'flex' }}>
                <span style={{ marginRight: '5px' }}>.</span>
                <span>{action}</span>
              </div>
            ))}
          </div>
        )}
      </div>}
    </div>
  );
};

export const brokerHealthPopoverContent = (
        <div style={{ padding: '4px' }}>
            <div style={{ marginBottom: '8px' }}>
                <strong>Health State Metrics:</strong>
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                    <li>Queue Count</li>
                    <li>Vhost Count</li>
                    <li>Shovel Count</li>
                    <li>Connection Count</li>
                    <li>ConsumerPerChannel Count</li>
                    <li>Channel Count</li>
                </ul>
            </div>
            <div>
                <strong>Classification Rules:</strong>
                <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                    <li><strong style={{ color: '#D13212' }}>Red:</strong> Broker is quarantined</li>
                    <li><strong style={{ color: '#DFAD0A' }}>Yellow:</strong> One or more metrics count &gt; limit</li>
                    <li><strong style={{ color: '#037f0c' }}>Green:</strong> Safe</li>
                </ul>
            </div>
            <div> The health state refreshes every 5 minutes</div>
            <div> Go to Service Metrics -&gt; Broker Metrics to view the historic health state graph</div>
        </div>
    );


function getColorForState(state: string): string {
  const normalizedState = state?.toLowerCase();
  switch (normalizedState) {
    case 'red':
      return '#D13212';
    case 'yellow': 
      return '#FF9900';
    case 'green':
      return '#037f0c';
    default:
      return '#687078';
  }
}

interface BrokerHealthStateComponentProps {
  brokerId: string;
  brokerEngineType?: string;
  currentDomain?: string;
}

export const BrokerHealthStateComponent: React.FC<BrokerHealthStateComponentProps> = ({ brokerId, brokerEngineType, currentDomain }) => {
  const [brokerHealthState, setBrokerHealthState] = useState<string | null>(null);
  const [brokerHealthRequiredActions, setRequiredActions] = useState<string | null>(null);
  const [brokerHealthRecommendedActions, setRecommendedActions] = useState<string | null>(null);
  const [lastUpdatedTime, setLastUpdatedTime] = useState<string | null>(null);
  const [loadingBrokerHealthState, setLoadingBrokerHealthState] = useState(false);
  const [healthStateTimeFrom, setHealthStateTimeFrom] = useState<Date>(new Date(Date.now() - 5 * 60 * 1000));
  const [healthStateTimeTo, setHealthStateTimeTo] = useState<Date>(new Date());
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>("5");
  const [customHours, setCustomHours] = useState<string>("");
  const [useCustomHours, setUseCustomHours] = useState<boolean>(false);
  const [lambdaNotFound, setLambdaNotFound] = useState<boolean>(false);

  const brokerHealthWithPopover = loadingBrokerHealthState ? (
    <Spinner />
  ) : (
    <HealthStateIndicator 
      healthState={brokerHealthState || ''}
      requiredActions={brokerHealthRequiredActions || ''}
      recommendedActions={brokerHealthRecommendedActions || ''}
      lastUpdatedTime={lastUpdatedTime || ''}
    />
  );

  const fetchBrokerHealthState = async () => {
    try {
      setLoadingBrokerHealthState(true);
      const response = await getBrokerHealthState(
        brokerId,
        healthStateTimeFrom,
        healthStateTimeTo
      );
      setBrokerHealthState(response.healthState || null);
      setRequiredActions(response.requiredActions?.join(', ') || null);
      setRecommendedActions(response.recommendedActions?.join(', ') || null);
      setLastUpdatedTime(dayjs().format('MMM DD, YYYY HH:mm:ss'));
      
      const recommendedActionsStr = response.recommendedActions?.join(', ') || '';
      if (response.healthState === 'Unknown' && 
          recommendedActionsStr.includes('Failed to invoke BrokerHealthStateReaderLambda')) {
        setLambdaNotFound(true);
      } else {
        setLambdaNotFound(false);
      }
    } catch (error) {
      setBrokerHealthState("Couldn't load");
      setLambdaNotFound(false);
    } finally {
      setLoadingBrokerHealthState(false);
    }
  };

  const handleTimeRangeChange = (from: Date, to: Date) => {
    setHealthStateTimeFrom(from);
    setHealthStateTimeTo(to);
  };

  const handleCustomHoursChange = (hours: string) => {
    const hoursNum = parseInt(hours, 10);
    if (hoursNum >= 1 && hoursNum <= 24) {
      const to = new Date();
      const from = new Date(to.getTime() - hoursNum * 60 * 60 * 1000);
      handleTimeRangeChange(from, to);
      setSelectedTimeRange("custom");
    }
  };

  const handleCustomHoursSubmit = () => {
    if (customHours && !isNaN(parseInt(customHours, 10))) {
      handleCustomHoursChange(customHours);
    }
  };

  const handleResetToDefault = () => {
    setSelectedTimeRange("5");
    setUseCustomHours(false);
    setCustomHours("");
    const to = new Date();
    const from = new Date(to.getTime() - 5 * 60 * 1000);
    handleTimeRangeChange(from, to);
  };

  useEffect(() => {
    if (brokerEngineType === 'RABBITMQ') {
      fetchBrokerHealthState();
      const intervalId = setInterval(fetchBrokerHealthState, 300000);
      return () => clearInterval(intervalId);
    }
  }, [brokerId, healthStateTimeFrom, healthStateTimeTo, brokerEngineType, currentDomain]);

  if (lambdaNotFound) {
    return null;
  }

  return (
    <div key="Broker Health State">
      <div className="awsui-util-label">
        <SpaceBetween direction="horizontal" size="xs">
          <strong>Broker Health State</strong>
          <Popover content={brokerHealthPopoverContent} triggerType="text-inline">
            <StatusIndicator type="info" />
          </Popover>
          {brokerHealthState && <CopyToClipboardButton text={brokerHealthState} />}
        </SpaceBetween>
      </div>
      <SpaceBetween direction="vertical" size="s">
        <SpaceBetween direction="horizontal" size="xs">
          <Select
            selectedOption={{ 
              label: selectedTimeRange === "custom" ? `Custom: ${customHours} hours` : `Last ${selectedTimeRange === "60" ? "1 hour" : selectedTimeRange === "1440" ? "24 hours" : selectedTimeRange + " minutes"}`, 
              value: selectedTimeRange 
            }}
            onChange={({ detail }) => {
              if (detail.selectedOption.value === "custom") {
                setUseCustomHours(true);
                setSelectedTimeRange("custom");
              } else {
                setUseCustomHours(false);
                const minutes = parseInt(detail.selectedOption.value || "5", 10);
                setSelectedTimeRange(detail.selectedOption.value || "5");
                const to = new Date();
                const from = new Date(to.getTime() - minutes * 60 * 1000);
                handleTimeRangeChange(from, to);
              }
            }}
            options={[
              { label: "Last 5 minutes", value: "5" },
              { label: "Last 15 minutes", value: "15" },
              { label: "Last 30 minutes", value: "30" },
              { label: "Last 1 hour", value: "60" },
              { label: "Last 24 hours", value: "1440" },
              { label: "Custom range", value: "custom" }
            ]}
          />
          {useCustomHours && (
            <>
              <div style={{ width: '150px' }}>
                <Input
                  value={customHours}
                  onChange={({ detail }) => setCustomHours(detail.value)}
                  placeholder="Enter hours (1-24)"
                  type="number"
                  inputMode="numeric"
                  onKeyDown={(event) => {
                    if (event.detail.key === 'Enter') {
                      handleCustomHoursSubmit();
                    }
                  }}
                />
              </div>
              <Button 
                onClick={handleCustomHoursSubmit}
                disabled={!customHours || isNaN(parseInt(customHours, 10)) || parseInt(customHours, 10) < 1 || parseInt(customHours, 10) > 24}
              >
                Apply
              </Button>
              <Button 
                variant="normal"
                onClick={handleResetToDefault}
              >
                Clear
              </Button>
            </>
          )}
          <Button 
            iconName="refresh" 
            onClick={fetchBrokerHealthState}
            disabled={loadingBrokerHealthState}
          />
        </SpaceBetween>
        <div>
          <span>{brokerHealthWithPopover}</span>
        </div>
      </SpaceBetween>
    </div>
  );
};
